import React from "react";
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Rewind,
  FastForward,
} from "lucide-react";
import { useRouteStore } from "../../store/useRouteStore";
import { useRoutePlayer } from "../../hooks/useRoutePlayer";
import { formatDuration, formatDateTime } from "../../utils/formatters";

export const RoutePlayer: React.FC = () => {
  const {
    selectedRoute,
    playerState,
    playRoute,
    pauseRoute,
    seekTo,
    setPlaybackSpeed,
  } = useRouteStore();

  const { getCurrentPosition, getPlaybackInfo } = useRoutePlayer();

  if (!selectedRoute) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <p className="text-gray-500 text-center">
          Select a route to use the player
        </p>
      </div>
    );
  }

  const currentPosition = getCurrentPosition();
  const playbackInfo = getPlaybackInfo();
  const speedOptions = [0.5, 1, 2, 4, 8];

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    seekTo(parseInt(e.target.value, 10));
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Current Position Info */}
      {currentPosition && (
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Time:</span>
              <div className="font-medium">
                {formatDateTime(currentPosition.timestamp)}
              </div>
            </div>
            <div>
              <span className="text-gray-600">Speed:</span>
              <div className="font-medium">
                {currentPosition.isInTransit
                  ? "In transit"
                  : `${currentPosition.speed?.toFixed(1) || "0"} km/h`}
              </div>
            </div>
            <div>
              <span className="text-gray-600">Battery:</span>
              <div className="font-medium">
                {currentPosition.battery_level ?? "N/A"}
                {currentPosition.battery_level !== undefined ? "%" : ""}
              </div>
            </div>
            <div>
              <span className="text-gray-600">Position:</span>
              <div className="font-medium">
                {currentPosition.latitude.toFixed(4)},{" "}
                {currentPosition.longitude.toFixed(4)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      <div className="p-4">
        <div className="flex items-center space-x-3">
          <span className="text-sm text-gray-600 w-16">
            {playbackInfo
              ? formatDuration(playbackInfo.currentDuration)
              : "00:00"}
          </span>

          <div className="flex-1">
            <input
              type="range"
              min="0"
              max="100"
              value={playerState.progress}
              onChange={handleSeek}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${playerState.progress}%, #e5e7eb ${playerState.progress}%, #e5e7eb 100%)`,
              }}
            />
          </div>

          <span className="text-sm text-gray-600 w-16 text-right">
            {playbackInfo
              ? formatDuration(playbackInfo.totalDuration)
              : "00:00"}
          </span>
        </div>
      </div>

      <div className="p-4 border-t border-gray-200">
        <div className="flex items-center justify-center space-x-4">
          <div className="flex items-center space-x-1">
            <span className="text-sm text-gray-600 sr-only">Speed:</span>
            <select
              value={playerState.playbackSpeed}
              onChange={(e) => setPlaybackSpeed(Number(e.target.value))}
              className="text-sm border border-gray-300 rounded px-2 py-1"
            >
              {speedOptions.map((speed) => (
                <option key={speed} value={speed}>
                  {speed}x
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => seekTo(0)}
              className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              title="Go to start"
            >
              <SkipBack className="w-5 h-5" />
            </button>

            <button
              onClick={() => seekTo(Math.max(0, playerState.progress - 10))}
              className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              title="Rewind 10%"
            >
              <Rewind className="w-5 h-5" />
            </button>

            <button
              onClick={playerState.isPlaying ? pauseRoute : playRoute}
              className="p-3 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              {playerState.isPlaying ? (
                <Pause className="w-6 h-6" />
              ) : (
                <Play className="w-6 h-6" />
              )}
            </button>

            <button
              onClick={() => seekTo(Math.min(100, playerState.progress + 10))}
              className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              title="Forward 10%"
            >
              <FastForward className="w-5 h-5" />
            </button>

            <button
              onClick={() => seekTo(100)}
              className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              title="Go to end"
            >
              <SkipForward className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
